// src/components/invoices/forms/InvoiceForm.jsx
import React, { useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CalendarIcon, Plus, Trash2, User, DollarSign, FileText, X, Gift, CheckCircle } from 'lucide-react';

// Import shadcn components
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../../ui/command";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Separator } from "../../ui/separator";
import { Badge } from "../../ui/badge";
import { Checkbox } from "../../ui/checkbox";
import { cn } from "../../../utils/cn";
import { format } from "date-fns";

// Invoice validation schema
const invoiceSchema = z.object({
  customer_id: z.string().min(1, 'Please select a customer'),
  invoice_date: z.date({
    required_error: 'Invoice date is required',
  }),
  due_date: z.date({
    required_error: 'Due date is required',
  }),
  payment_terms: z.string().min(1, 'Payment terms are required'),
  line_items: z.array(z.object({
    description: z.string().min(1, 'Description is required'),
    quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
    unit_price: z.number().min(0, 'Unit price must be 0 or greater'),
  })).min(1, 'At least one line item is required'),
  notes: z.string().optional(),
  event_ids: z.array(z.string()).optional(),
});

const InvoiceForm = ({
  invoice = null,
  customers = [],
  onSubmit,
  onCancel,
  loading = false,
  isEditing = false,
  initialCustomerId = null,
  preSelectedAppointments = [],
  getCustomerEvents = () => [],
  availableCredits = [],
  onCreditSelect = null,
  onCustomerChange = null
}) => {
  // State for selected credit
  const [selectedCreditId, setSelectedCreditId] = React.useState(null);

  // Reset selected credit when customer changes
  React.useEffect(() => {
    setSelectedCreditId(null);
  }, [availableCredits]);
  // Initialize form with default values
  const form = useForm({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      customer_id: invoice?.customer_id || initialCustomerId || '',
      invoice_date: invoice?.invoice_date ? new Date(invoice.invoice_date) : new Date(),
      due_date: invoice?.due_date ? new Date(invoice.due_date) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      payment_terms: invoice?.payment_terms || 'Net 30',
      line_items: invoice?.line_items || [{
        description: 'Service',
        quantity: 1,
        unit_price: 0
      }],
      notes: invoice?.notes || '',
      event_ids: invoice?.invoice_events?.map(ie => ie.event_id) || preSelectedAppointments || [],
    },
  });

  // Dynamic line items
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "line_items"
  });

  // Payment terms options
  const paymentTermsOptions = [
    { value: 'Due on receipt', label: 'Due on receipt' },
    { value: 'Net 15', label: 'Net 15' },
    { value: 'Net 30', label: 'Net 30' },
    { value: 'Net 45', label: 'Net 45' },
    { value: 'Net 60', label: 'Net 60' },
  ];

  // Watch line items for total calculation
  const lineItems = form.watch('line_items');

  // Watch customer_id to filter appointments
  const selectedCustomerId = form.watch('customer_id');
  const selectedAppointmentIds = form.watch('event_ids');

  // Get customer's appointments (memoized)
  const customerAppointments = useMemo(() => {
    if (!selectedCustomerId) return [];
    return getCustomerEvents(selectedCustomerId).filter(event =>
      event.event_category === 'service' // Only service events (appointments)
    );
  }, [selectedCustomerId, getCustomerEvents]);

  // Get selected appointment objects
  const selectedAppointments = useMemo(() => {
    return customerAppointments.filter(appt =>
      selectedAppointmentIds?.includes(appt.id)
    );
  }, [customerAppointments, selectedAppointmentIds]);

  // Calculate totals
  const subtotal = lineItems.reduce((sum, item) => {
    return sum + (item.quantity * item.unit_price);
  }, 0);

  const taxRate = 0.0825; // 8.25% - TODO: Make configurable
  const taxAmount = subtotal * taxRate;

  // Get selected credit amount
  const selectedCredit = availableCredits.find(c => c.id === selectedCreditId);
  const creditAmount = selectedCredit ? parseFloat(selectedCredit.amount) : 0;

  const total = Math.max(0, subtotal + taxAmount - creditAmount);

  // Form submission
  const handleSubmit = (data) => {
    onSubmit({
      ...data,
      invoice_date: data.invoice_date.toISOString().split('T')[0],
      due_date: data.due_date.toISOString().split('T')[0],
      event_ids: data.event_ids || [],
      credit_id: selectedCreditId || null,
    });
  };

  // Add new line item
  const addLineItem = () => {
    append({
      description: '',
      quantity: 1,
      unit_price: 0
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">

        {/* Customer Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Information
            </CardTitle>
            <CardDescription>
              Select the customer for this invoice
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="customer_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (onCustomerChange) {
                        onCustomerChange(value);
                      }
                    }}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a customer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{customer.name}</span>
                            <span className="text-sm text-muted-foreground">{customer.email}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Dates and Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Invoice Details
            </CardTitle>
            <CardDescription>
              Set the invoice and due dates, and payment terms
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Invoice Date */}
            <FormField
              control={form.control}
              name="invoice_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Invoice Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date < new Date("1900-01-01")}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Due Date */}
            <FormField
              control={form.control}
              name="due_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Due Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date < new Date("1900-01-01")}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment Terms */}
            <FormField
              control={form.control}
              name="payment_terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Terms</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment terms" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {paymentTermsOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Linked Appointments Section */}
        {selectedCustomerId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Linked Appointments
              </CardTitle>
              <CardDescription>
                {customerAppointments.length > 0
                  ? 'Link this invoice to customer appointments (optional)'
                  : 'No appointments found for this customer'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {customerAppointments.length > 0 ? (
                <FormField
                  control={form.control}
                  name="event_ids"
                  render={({ field }) => (
                    <FormItem>
                      <div className="space-y-3">
                        {/* Display selected appointments as removable badges */}
                        {selectedAppointments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {selectedAppointments.map(appt => (
                              <Badge
                                key={appt.id}
                                variant="secondary"
                                className="flex items-center gap-2 py-1.5 px-3"
                              >
                                <CalendarIcon className="h-3 w-3" />
                                <span>
                                  {format(new Date(appt.scheduled_datetime), "MMM d, yyyy")} - {appt.service_type}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newIds = field.value.filter(id => id !== appt.id);
                                    field.onChange(newIds);
                                  }}
                                  className="hover:bg-muted rounded-full p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Available appointments list */}
                        <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-3">
                          {customerAppointments.map(appointment => {
                            const isSelected = field.value?.includes(appointment.id);
                            const appointmentDate = new Date(appointment.scheduled_datetime);

                            return (
                              <div
                                key={appointment.id}
                                className={cn(
                                  "flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors",
                                  isSelected
                                    ? "bg-primary/10 border-primary"
                                    : "hover:bg-muted"
                                )}
                                onClick={() => {
                                  const newIds = isSelected
                                    ? field.value.filter(id => id !== appointment.id)
                                    : [...(field.value || []), appointment.id];
                                  field.onChange(newIds);
                                }}
                              >
                                <div onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={(checked) => {
                                      const newIds = checked
                                        ? [...(field.value || []), appointment.id]
                                        : field.value.filter(id => id !== appointment.id);
                                      field.onChange(newIds);
                                    }}
                                  />
                                </div>
                                <div className="flex-1 space-y-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">
                                      {appointment.service_type}
                                    </span>
                                    <Badge
                                      variant={
                                        appointment.status === 'completed' ? 'default' :
                                        appointment.status === 'confirmed' ? 'secondary' :
                                        'outline'
                                      }
                                      className="text-xs"
                                    >
                                      {appointment.status}
                                    </Badge>
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {format(appointmentDate, "MMMM d, yyyy 'at' h:mm a")}
                                  </div>
                                  {appointment.service_address && (
                                    <div className="text-xs text-muted-foreground">
                                      {appointment.service_address}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Create appointments for this customer first to link them to invoices.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Line Items
            </CardTitle>
            <CardDescription>
              Add services and products to this invoice
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end p-4 border rounded-lg">

                {/* Description */}
                <div className="md:col-span-6">
                  <FormField
                    control={form.control}
                    name={`line_items.${index}.description`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Input placeholder="Service description" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Quantity */}
                <div className="md:col-span-2">
                  <FormField
                    control={form.control}
                    name={`line_items.${index}.quantity`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quantity</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Unit Price */}
                <div className="md:col-span-2">
                  <FormField
                    control={form.control}
                    name={`line_items.${index}.unit_price`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unit Price</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Line Total & Remove Button */}
                <div className="md:col-span-2 flex items-center justify-between">
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Total</div>
                    <div className="font-medium">
                      ${((lineItems[index]?.quantity || 0) * (lineItems[index]?.unit_price || 0)).toFixed(2)}
                    </div>
                  </div>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                      className="text-destructive hover:text-destructive ml-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {/* Add Line Item Button */}
            <Button type="button" variant="outline" onClick={addLineItem} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Line Item
            </Button>

            <Separator />

            {/* Available Credits */}
            {availableCredits.length > 0 && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-400 font-medium">
                  <Gift className="h-4 w-4" />
                  Available Credits
                </div>
                <div className="space-y-2">
                  {availableCredits.map((credit) => (
                    <div
                      key={credit.id}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedCreditId === credit.id
                          ? "bg-green-500/20 border-green-500"
                          : "bg-background/50 border-border hover:border-green-500/50"
                      )}
                      onClick={() => {
                        setSelectedCreditId(selectedCreditId === credit.id ? null : credit.id);
                        if (onCreditSelect) {
                          onCreditSelect(selectedCreditId === credit.id ? null : credit);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                          selectedCreditId === credit.id
                            ? "border-green-500 bg-green-500"
                            : "border-muted-foreground"
                        )}>
                          {selectedCreditId === credit.id && (
                            <CheckCircle className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium">${parseFloat(credit.amount).toFixed(2)} Credit</div>
                          <div className="text-xs text-muted-foreground">
                            {credit.description || 'Referral reward'}
                          </div>
                        </div>
                      </div>
                      {selectedCreditId === credit.id && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          Applied
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax (8.25%):</span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
                {creditAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Credit Applied:</span>
                    <span>-${creditAmount.toFixed(2)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-medium">
                  <span>Total:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Notes</CardTitle>
            <CardDescription>
              Add any additional information or terms for this invoice
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder="Add any additional notes or terms..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : isEditing ? 'Update Invoice' : 'Create Invoice'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default InvoiceForm;